import { useCallback } from "react";

/**
 * Rollover override tab for per-tenant rollover assumptions.
 *
 * Each field has a "Use Global Default" toggle. When toggled on, the value
 * is null (falls back to portfolio defaults). When toggled off, the user
 * can enter a tenant-specific override value.
 */

export interface RolloverOverrides {
  renewal_probability: number | null;
  market_rent_psf: number | null;
  market_rent_growth: number | null;
  lease_term_years: number | null;
  downtime_new_months: number | null;
  downtime_renewal_months: number | null;
  ti_new_psf: number | null;
  ti_renewal_psf: number | null;
  lc_new_pct: number | null;
  lc_renewal_pct: number | null;
  upon_expiration: string | null;
}

interface RolloverTabProps {
  overrides: RolloverOverrides;
  onChange: (key: keyof RolloverOverrides, value: unknown) => void;
}

const UPON_EXPIRATION_OPTIONS = [
  { value: "renew_or_release", label: "Renew or Re-lease" },
  { value: "go_dark", label: "Go Dark" },
  { value: "vacate", label: "Vacate" },
];

interface OverrideFieldProps {
  label: string;
  value: number | string | null;
  onChange: (val: number | string | null) => void;
  type?: "number" | "select";
  prefix?: string;
  suffix?: string;
  placeholder?: string;
  options?: Array<{ value: string; label: string }>;
  step?: string;
}

function OverrideField({
  label,
  value,
  onChange,
  type = "number",
  prefix,
  suffix,
  placeholder = "Use default",
  options,
  step = "any",
}: OverrideFieldProps) {
  const isDefault = value === null || value === undefined;

  const handleToggle = useCallback(() => {
    if (isDefault) {
      // Switch to override: set a sensible default
      if (type === "select" && options?.length) {
        onChange(options[0].value);
      } else {
        onChange(0);
      }
    } else {
      onChange(null);
    }
  }, [isDefault, type, options, onChange]);

  return (
    <div className="flex items-center gap-3 py-2.5 border-b border-grove-200 last:border-0">
      <div className="flex-1 min-w-0">
        <span className="text-sm text-bark-600">{label}</span>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <label className="inline-flex items-center gap-1.5 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={isDefault}
            onChange={handleToggle}
            className="w-3.5 h-3.5 rounded border-grove-300 text-forest-600 focus:ring-forest-500"
          />
          <span className="text-xs text-bark-400 whitespace-nowrap">
            Use Global Default
          </span>
        </label>
        <div className="w-40">
          {isDefault ? (
            <span className="block text-sm text-bark-300 text-right">
              Global
            </span>
          ) : type === "select" && options ? (
            <select
              value={String(value)}
              onChange={(e) => onChange(e.target.value)}
              className="w-full px-2 py-1 text-sm bg-white border border-grove-300 rounded-lg text-bark-700 focus:ring-1 focus:ring-forest-500 focus:border-forest-500"
            >
              {options.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          ) : (
            <div className="relative">
              {prefix && (
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-sm text-bark-400">
                  {prefix}
                </span>
              )}
              <input
                type="number"
                step={step}
                value={value ?? ""}
                onChange={(e) =>
                  onChange(e.target.value === "" ? null : Number(e.target.value))
                }
                placeholder={placeholder}
                className={`w-full py-1 text-sm text-right border border-grove-300 rounded-lg text-bark-700 tabular-nums focus:ring-1 focus:ring-forest-500 focus:border-forest-500 ${
                  prefix ? "pl-5 pr-2" : suffix ? "pl-2 pr-6" : "px-2"
                }`}
              />
              {suffix && (
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-sm text-bark-400">
                  {suffix}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function RolloverTab({ overrides, onChange }: RolloverTabProps) {
  return (
    <div className="space-y-5">
      <div className="bg-white rounded-xl border border-grove-300 p-5 shadow-sm">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-bark-400 mb-1">
          Per-Tenant Rollover Overrides
        </h3>
        <p className="text-sm text-bark-400 mb-4">
          Override the global rollover assumptions for this tenant. Unchecked
          fields fall back to deal-level defaults.
        </p>

        <div className="divide-y divide-grove-200">
          <OverrideField
            label="Renewal Probability (%)"
            value={overrides.renewal_probability}
            onChange={(v) => onChange("renewal_probability", v)}
            suffix="%"
            step="1"
          />
          <OverrideField
            label="Market Rent at Expiry ($/SF/Yr)"
            value={overrides.market_rent_psf}
            onChange={(v) => onChange("market_rent_psf", v)}
            prefix="$"
          />
          <OverrideField
            label="Market Rent Growth (%/yr)"
            value={overrides.market_rent_growth}
            onChange={(v) => onChange("market_rent_growth", v)}
            suffix="%"
          />
          <OverrideField
            label="New Lease Term (years)"
            value={overrides.lease_term_years}
            onChange={(v) => onChange("lease_term_years", v)}
            suffix="yr"
            step="1"
          />
          <OverrideField
            label="Downtime - New Tenant (months)"
            value={overrides.downtime_new_months}
            onChange={(v) => onChange("downtime_new_months", v)}
            suffix="mo"
            step="1"
          />
          <OverrideField
            label="Downtime - Renewal (months)"
            value={overrides.downtime_renewal_months}
            onChange={(v) => onChange("downtime_renewal_months", v)}
            suffix="mo"
            step="1"
          />
          <OverrideField
            label="TI - New Tenant ($/SF)"
            value={overrides.ti_new_psf}
            onChange={(v) => onChange("ti_new_psf", v)}
            prefix="$"
          />
          <OverrideField
            label="TI - Renewal ($/SF)"
            value={overrides.ti_renewal_psf}
            onChange={(v) => onChange("ti_renewal_psf", v)}
            prefix="$"
          />
          <OverrideField
            label="LC - New Tenant (%)"
            value={overrides.lc_new_pct}
            onChange={(v) => onChange("lc_new_pct", v)}
            suffix="%"
          />
          <OverrideField
            label="LC - Renewal (%)"
            value={overrides.lc_renewal_pct}
            onChange={(v) => onChange("lc_renewal_pct", v)}
            suffix="%"
          />
          <OverrideField
            label="Upon Expiration"
            value={overrides.upon_expiration}
            onChange={(v) => onChange("upon_expiration", v)}
            type="select"
            options={UPON_EXPIRATION_OPTIONS}
          />
        </div>
      </div>
    </div>
  );
}
